/**
 * DedicatedOffersList — Reusable list for "Offerte Dedicate Cliente".
 *
 * Used by:
 *  - Admin (tab "dedicated-offers")
 *  - Agent (/agent/dedicated-offers)
 *
 * Backend filters by role automatically: admin sees all in tenant; agent only sees
 * offers owned by him (`agent_id == agent.id`).
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, FileText, Edit, Archive, Trash2, Eye, Search, Sparkles, ArchiveRestore, Share2, Copy } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { openAuthenticatedPdf } from "../utils/pdf";
import DedicatedOfferDialog from "./DedicatedOfferDialog";
import BroadcastOfferDialog from "./BroadcastOfferDialog";
import DuplicateOfferDialog from "./DuplicateOfferDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DedicatedOffersList({ currentUser }) {
  const canBroadcast = currentUser?.role === "admin" || currentUser?.role === "warehouse";
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [editOffer, setEditOffer] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);
  const [duplicateOffer, setDuplicateOffer] = useState(null);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dedicated-offers`, { params: { include_archived: includeArchived } });
      setOffers(r.data || []);
    } catch (e) {
      toast.error("Errore nel caricamento offerte");
      console.debug(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, [includeArchived]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return offers;
    return offers.filter((o) =>
      (o.customer_name || "").toLowerCase().includes(q) ||
      (o.customer_codice || "").toLowerCase().includes(q) ||
      (o.pricelist_type_name || "").toLowerCase().includes(q) ||
      (o.customer_vat || "").toLowerCase().includes(q)
    );
  }, [offers, search]);

  const handleArchive = async (offer) => {
    if (!window.confirm(`Archiviare l'offerta dedicata a ${offer.customer_name}? Non sarà più visibile al cliente.`)) return;
    try {
      await axios.put(`${API}/dedicated-offers/${offer.id}`, { status: "archived" });
      toast.success("Offerta archiviata");
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleReactivate = async (offer) => {
    try {
      await axios.put(`${API}/dedicated-offers/${offer.id}`, { status: "active" });
      toast.success("Offerta riattivata");
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Eliminare definitivamente questa offerta? L'azione non è reversibile.`)) return;
    try {
      await axios.delete(`${API}/dedicated-offers/${offer.id}`);
      toast.success("Offerta eliminata");
      fetchOffers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleSaved = () => {
    fetchOffers();
  };

  return (
    <div className="space-y-4" data-testid="dedicated-offers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#C5A059]" /> Offerte Dedicate Cliente
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            Quotazioni multi-articolo con prezzi personalizzati per cliente. Il cliente può ordinare direttamente con questi prezzi.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canBroadcast && (
            <Button onClick={() => setBroadcastOpen(true)} variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" data-testid="dedicated-offer-broadcast-btn">
              <Share2 className="w-4 h-4 mr-2" />Broadcast
            </Button>
          )}
          <Button onClick={() => { setEditOffer(null); setCreateOpen(true); }} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="dedicated-offer-new-btn">
            <Plus className="w-4 h-4 mr-2" />Nuova offerta dedicata
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            className="pl-10"
            placeholder="Cerca per cliente, codice o P.IVA..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="dedicated-offer-search"
          />
        </div>
        <Button
          variant={includeArchived ? "default" : "outline"}
          onClick={() => setIncludeArchived(!includeArchived)}
          data-testid="dedicated-offer-toggle-archived"
          className={includeArchived ? "bg-stone-700 text-white" : ""}
        >
          <Archive className="w-4 h-4 mr-2" />{includeArchived ? "Nascondi archiviate" : "Mostra archiviate"}
        </Button>
      </div>

      {loading && <div className="text-center py-12 text-stone-500 text-sm">Caricamento...</div>}

      {!loading && filtered.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">Nessuna offerta dedicata.</p>
            <p className="text-xs text-stone-400 mt-1">Crea la prima offerta per uno dei tuoi clienti.</p>
          </CardContent>
        </Card>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dedicated-offers-grid">
          {filtered.map((o) => {
            const itemsCount = (o.items || []).length;
            const avgDiscount = itemsCount > 0
              ? (o.items.reduce((s, i) => s + (Number(i.discount_pct) || 0), 0) / itemsCount).toFixed(1)
              : "0";
            const isArchived = o.status === "archived";
            return (
              <Card key={o.id} className={`relative overflow-hidden hover:shadow-md transition ${isArchived ? "opacity-60" : ""}`} data-testid={`dedicated-offer-${o.id}`}>
                <div className={`absolute top-0 left-0 right-0 h-1 ${isArchived ? "bg-stone-400" : "bg-gradient-to-r from-[#8A1C2A] to-[#C5A059]"}`} />
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold">
                        {o.is_prospect ? "Prospect" : "Cliente"}
                      </p>
                      <p className="font-semibold text-stone-900 truncate">{o.customer_name}</p>
                      <p className="text-xs text-stone-500">
                        {o.is_prospect
                          ? `${o.customer_vat || "—"}${o.language === "en" ? " · EN" : ""}`
                          : `${o.customer_codice} · ${o.customer_vat || "—"}`}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {o.is_prospect && (
                        <Badge className="bg-[#C5A059] text-white text-[10px]" data-testid={`do-prospect-badge-${o.id}`}>
                          <Sparkles className="w-3 h-3 mr-1" />PROSPECT
                        </Badge>
                      )}
                      {isArchived && <Badge variant="outline" className="text-stone-500 text-[10px]">ARCHIVIATA</Badge>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 py-2 border-y border-stone-100">
                    <div>
                      <p className="text-[10px] text-stone-500 uppercase">Listino</p>
                      <p className="text-sm font-medium text-stone-700 truncate">{o.pricelist_type_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-stone-500 uppercase">Articoli</p>
                      <p className="text-sm font-medium text-stone-700">{itemsCount}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-stone-500 uppercase">Sconto medio</p>
                      <p className="text-sm font-bold text-[#C5A059]">−{avgDiscount}%</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-stone-400">
                    Creata {new Date(o.created_at).toLocaleDateString("it-IT")} da {o.created_by_name}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <Button size="sm" variant="outline" onClick={() => setPreviewOffer(o)} data-testid={`do-view-${o.id}`}>
                      <Eye className="w-3.5 h-3.5 mr-1" />Vedi
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openAuthenticatedPdf(`/dedicated-offers/${o.id}/pdf`)} data-testid={`do-pdf-${o.id}`}>
                      <FileText className="w-3.5 h-3.5 mr-1" />PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setDuplicateOffer(o)} data-testid={`do-duplicate-${o.id}`}>
                      <Copy className="w-3.5 h-3.5 mr-1" />Duplica
                    </Button>
                    {!isArchived && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => { setEditOffer(o); setCreateOpen(true); }} data-testid={`do-edit-${o.id}`}>
                          <Edit className="w-3.5 h-3.5 mr-1" />Modifica
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleArchive(o)} data-testid={`do-archive-${o.id}`}>
                          <Archive className="w-3.5 h-3.5 mr-1" />Archivia
                        </Button>
                      </>
                    )}
                    {isArchived && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleReactivate(o)} data-testid={`do-restore-${o.id}`}>
                          <ArchiveRestore className="w-3.5 h-3.5 mr-1" />Riattiva
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDelete(o)} className="text-rose-600 hover:bg-rose-50 hover:border-rose-200" data-testid={`do-delete-${o.id}`}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Elimina
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DedicatedOfferDialog
        open={createOpen}
        editOffer={editOffer}
        onClose={() => { setCreateOpen(false); setEditOffer(null); }}
        onSaved={handleSaved}
      />

      {canBroadcast && (
        <BroadcastOfferDialog
          open={broadcastOpen}
          onClose={() => setBroadcastOpen(false)}
          onSaved={() => { setBroadcastOpen(false); fetchOffers(); }}
        />
      )}

      <DuplicateOfferDialog
        open={!!duplicateOffer}
        source={duplicateOffer}
        onClose={() => setDuplicateOffer(null)}
        onDuplicated={() => { setDuplicateOffer(null); fetchOffers(); }}
      />

      {/* Preview dialog */}
      <Dialog open={!!previewOffer} onOpenChange={(v) => !v && setPreviewOffer(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="do-preview-dialog">
          {previewOffer && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">
                  Offerta dedicata · {previewOffer.customer_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-stone-500">Codice:</span> {previewOffer.customer_codice}</div>
                  <div><span className="text-stone-500">P.IVA:</span> {previewOffer.customer_vat || "—"}</div>
                  <div><span className="text-stone-500">Listino base:</span> {previewOffer.pricelist_type_name}</div>
                  <div><span className="text-stone-500">Articoli:</span> {(previewOffer.items || []).length}</div>
                </div>
                {previewOffer.notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-stone-700">
                    <span className="font-medium">Note:</span> {previewOffer.notes}
                  </div>
                )}
                <div className="border border-stone-200 rounded-lg overflow-hidden overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead className="bg-stone-100">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600">Codice</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600">Categoria</th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-stone-600">Descrizione</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-stone-600">Listino</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-stone-600">Sconto</th>
                        <th className="text-right px-3 py-2 text-xs font-semibold text-stone-600">Dedicato</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {(previewOffer.items || []).map((it, idx) => (
                        <tr key={it.product_id || `free-${idx}`}>
                          <td className="px-3 py-1.5 font-mono text-xs">{it.code || "—"}</td>
                          <td className="px-3 py-1.5 text-xs text-stone-500">{it.category_name || "—"}</td>
                          <td className="px-3 py-1.5 text-sm">{it.description}{it.is_free && <span className="ml-2 text-[9px] uppercase text-amber-700 font-semibold">LIBERO</span>}</td>
                          <td className="px-3 py-1.5 text-right text-stone-600">€ {Number(it.base_price).toFixed(2)}</td>
                          <td className="px-3 py-1.5 text-right text-[#C5A059] font-medium">−{Number(it.discount_pct).toFixed(1)}%</td>
                          <td className="px-3 py-1.5 text-right text-[#8A1C2A] font-bold">€ {Number(it.final_price).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => openAuthenticatedPdf(`/dedicated-offers/${previewOffer.id}/pdf`)}>
                  <FileText className="w-4 h-4 mr-2" />Apri PDF
                </Button>
                <Button onClick={() => setPreviewOffer(null)}>Chiudi</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
